import { useEffect, useState, useCallback, Activity } from 'react';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
  NavLink,
} from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import isEqual from 'lodash.isequal';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  SidePanelLoader,
  ButtonSecondary,
  Select,
  InputText,
  InputSize,
  Checkbox,
  Currency,
  IconSearch,
  Popup,
  usePrevious,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
  PaymentType,
  IconArrowRight,
  IconAnimatedDots,
  Tabs,
  ChangeLogs,
  PermissionDenied,
  IconExpand,
  ButtonSize,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetConfigurationAccountBalanceQuery,
  useUpdatePspExternalAccountStatusMutation,
  useLazyGetPSPExternalAccountQuery,
  useLazyGetPspExternalAccountChangeLogsQuery,
  useLazyGetPspExternalAccountTopUpOptionsQuery,
  useUpdatePspExternalAccountCurrenciesMutation,
  useCreatePSPPaymentsAccountsMutation,
  useUpdatePSPPaymentsAccountsMutation,
  useDeletePSPPaymentAccountsMutation,
} from './store/services';
import {
  Balance,
  ExternalAccountPageTypes,
  ExternalAccountTopUpOptionFormValues,
} from './types';
import { clearExternalAccountState } from './store/slices/info';
import ExternalAccountTopUpOptionItem from '../../components/bank-and-psp/top-op-option';

import { BankRoutes } from '../../routes';

import cn from './bank-and-psp.module.css';

const ExternalAccount = ({ accountPage }: ExternalAccountPageTypes) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [status, setStatus] = useState('');
  const [availablePaymentTypes, setAvailablePaymentTypes] = useState<string[]>(
    [],
  );
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [balances, setBalances] = useState<Balance[]>([]);
  const [availableCurrencies, setAvailableCurrencies] = useState<Currency[]>(
    [],
  );
  const [searchValue, setSearchValue] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [activeTab, setActiveTab] = useState<
    'details' | 'top-up' | 'change-logs'
  >('details');
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);
  const [isOpenPayd, setIsOpenPayd] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState('');
  const [defaultTopUpOptions, setDefaultTopUpOptions] = useState<
    ExternalAccountTopUpOptionFormValues[]
  >([]);
  const [topUpOptions, setTopUpOptions] = useState<
    ExternalAccountTopUpOptionFormValues[]
  >([]);
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] =
    useState(false);
  const [optionsErrors, setOptionsErrors] = useState<
    {
      id: string;
      currencyId?: string;
      paymentType?: string;
    }[]
  >([]);

  const {
    configurationDictionaries: { paymentTypes, activePermissions },
    bankAndPsp: {
      info: {
        casp,
        generalInfo,
        externalAccount,
        externalAccountTopUpOptions,
        externalChangeLogs,
        externalChangeLogsPagination,
      },
    },
    root: { currencies: dictionariesCurrencies, currenciesList },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);

  const prevStatus = usePrevious(status);

  const dispatch = useAppDispatch();

  const [getConfigurationBalances, { isLoading, isFetching }] =
    useLazyGetConfigurationAccountBalanceQuery();
  const [updateStatus, { isLoading: requestInProgress }] =
    useUpdatePspExternalAccountStatusMutation();
  const [
    getExternalAccount,
    {
      isLoading: externalAccountIsLoading,
      isFetching: externalAccountIsFetching,
    },
  ] = useLazyGetPSPExternalAccountQuery();
  const [
    getExternalAccountTopUpOptions,
    { isLoading: topUpIsLoading, isFetching: topUpIsFetching },
  ] = useLazyGetPspExternalAccountTopUpOptionsQuery();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetPspExternalAccountChangeLogsQuery();
  const [
    updatePspExternalAccountCurrencies,
    {
      isLoading: updatePspExternalAccountCurrenciesInProgress,
      isSuccess: updatePspExternalAccountCurrenciesIsSuccess,
    },
  ] = useUpdatePspExternalAccountCurrenciesMutation();
  const [
    deletePaymentAccounts,
    {
      isLoading: deletePaymentAccountsInProgress,
      isSuccess: deletePaymentAccountsIsSuccess,
    },
  ] = useDeletePSPPaymentAccountsMutation();
  const [
    createPaymentAccounts,
    {
      isLoading: createPaymentAccountsInProgress,
      isSuccess: createPaymentAccountsIsSuccess,
    },
  ] = useCreatePSPPaymentsAccountsMutation();
  const [
    updatePaymentAccounts,
    {
      isLoading: updatePaymentAccountsInProgress,
      isSuccess: updatePaymentAccountsIsSuccess,
    },
  ] = useUpdatePSPPaymentsAccountsMutation();

  const { search } = useLocation();
  const navigate = useNavigate();

  const { accountId, bankId, externalAccountId } = useParams();

  const isDirty =
    Boolean(prevStatus && status !== prevStatus) ||
    !isEqual(topUpOptions, defaultTopUpOptions) ||
    !isEqual(externalAccount?.currenciesIds, selectedCurrencies);

  const deletePaymentAccountsAction = async () => {
    await deletePaymentAccounts(selectedOptionId);

    if (optionsErrors?.find((item) => item.id === selectedOptionId)) {
      setOptionsErrors(
        optionsErrors?.filter((item) => item.id !== selectedOptionId),
      );
    }
  };

  const onSubmit = async () => {
    setGlobalError('');
    setOptionsErrors([]);

    const optionsForCreate: ExternalAccountTopUpOptionFormValues[] = [];
    const optionForUpdate: ExternalAccountTopUpOptionFormValues[] = [];
    const optionForWithErrors: ExternalAccountTopUpOptionFormValues[] = [];

    topUpOptions?.forEach((item) => {
      if (!item?.paymentType || !item?.currencyId) {
        optionForWithErrors.push(item);
      }

      if (item?.id?.includes('option')) {
        optionsForCreate.push(item);
      }

      if (!item?.id?.includes('option')) {
        const currentDefaultOption = defaultTopUpOptions?.find(
          (defaultOption) => defaultOption?.id === item?.id,
        );

        if (currentDefaultOption && !isEqual(currentDefaultOption, item)) {
          optionForUpdate.push(item);
        }
      }
    });

    if (!optionForWithErrors?.length) {
      if (prevStatus !== status) {
        const response = (await updateStatus({
          ids: [accountId as string],
          active: Boolean(status === 'enabled'),
        })) as { error?: ResponseError };

        if (response?.error) {
          responseErrorsResolver({
            errors: response?.error,
            setError: () => {},
            setGlobalError,
          });
        }
      }

      if (!isEqual(externalAccount?.currenciesIds, selectedCurrencies)) {
        const response = (await updatePspExternalAccountCurrencies({
          id: externalAccount?.id as string,
          currenciesIds: selectedCurrencies,
        })) as { error?: ResponseError };

        if (response?.error) {
          responseErrorsResolver({
            errors: response?.error,
            setError: () => {},
            setGlobalError,
          });
        }
      }

      if (optionsForCreate?.length) {
        await Promise.all(
          optionsForCreate?.map(async (item) => {
            const body: ExternalAccountTopUpOptionFormValues = {
              paymentType: item?.paymentType,
              currencyId: item?.currencyId,
              bankAccountHolderName: item?.bankAccountHolderName,
              accountNumber: item?.accountNumber,
              bankAddress: item?.bankAddress,
              bic: item?.bic,
              iban: item?.iban,
              bankName: item?.bankName,
              pspIntegrationAccountId: externalAccount?.id,
            };

            if (item?.thirdPartyReference) {
              body.thirdPartyReference = item.thirdPartyReference;
            }

            if (
              item?.routingCodeEntries?.filter(
                (code) => code?.routingCodeKey && code?.routingCodeValue,
              )?.length
            ) {
              body.routingCodeEntries = item?.routingCodeEntries
                ?.filter(
                  (code) => code?.routingCodeKey && code?.routingCodeValue,
                )
                ?.map((code) => ({
                  routingCodeKey: code.routingCodeKey,
                  routingCodeValue: code.routingCodeValue,
                }));
            }

            await createPaymentAccounts(body);
          }),
        );
      }

      if (optionForUpdate?.length) {
        await Promise.all(
          optionForUpdate?.map(async (item) => {
            const body: ExternalAccountTopUpOptionFormValues = {
              paymentType: item?.paymentType,
              currencyId: item?.currencyId,
              bankAccountHolderName: item?.bankAccountHolderName,
              accountNumber: item?.accountNumber,
              bankAddress: item?.bankAddress,
              bic: item?.bic,
              iban: item?.iban,
              bankName: item?.bankName,
            };

            if (item?.thirdPartyReference) {
              body.thirdPartyReference = item.thirdPartyReference;
            }

            if (
              item?.routingCodeEntries?.filter(
                (code) => code.routingCodeKey && code.routingCodeValue,
              )?.length
            ) {
              body.routingCodeEntries = item?.routingCodeEntries
                ?.filter((code) => code.routingCodeKey && code.routingCodeValue)
                ?.map((code) => ({
                  routingCodeKey: code.routingCodeKey,
                  routingCodeValue: code.routingCodeValue,
                }));
            }

            const currentDefaultTopUpOptions = defaultTopUpOptions?.find(
              (option) => option?.id === item?.id,
            );

            if (
              !item?.routingCodeEntries?.filter(
                (code) => code.routingCodeKey && code.routingCodeValue,
              ) &&
              currentDefaultTopUpOptions &&
              currentDefaultTopUpOptions?.routingCodeEntries?.length
            ) {
              body.routingCodeEntries = null;
            }

            await updatePaymentAccounts({
              id: item?.id as string,
              body,
            });
          }),
        );
      }
    } else {
      const optionErrors: {
        id: string;
        currencyId?: string;
        paymentType?: string;
      }[] = [];

      optionForWithErrors?.forEach((item) => {
        const body: {
          id: string;
          currencyId?: string;
          paymentType?: string;
        } = {
          id: item.id as string,
        };

        if (!item?.currencyId) {
          body.currencyId = 'Currency is required';
        }

        if (!item.paymentType) {
          body.paymentType = 'Payment Type is required';
        }

        optionErrors.push(body);
      });

      setOptionsErrors(optionErrors);
    }
  };

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=PSP_INTEGRATION_ACCOUNT&entityId=${accountId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [accountId],
  );

  const onSearch = (value: string) => {
    const nextCurrencies: Currency[] = value
      ? availableCurrencies?.filter((item) =>
          item.name.toLowerCase().includes(value.toLowerCase()),
        )
      : availableCurrencies?.map((item) => item);

    setSearchValue(value);

    setCurrencies(nextCurrencies);
  };

  const getAccountsBalancesAction = useCallback(async (id: string) => {
    const response = await getConfigurationBalances(id);

    if (response) {
      setBalances(
        response?.data?.data?.edges?.map((item) => item.node) as Balance[],
      );
    }
  }, []);

  const handleClose = () => {
    setGlobalError('');
    setPanelIsOpen(false);
    dispatch(clearExternalAccountState());

    let link;

    if (accountPage) {
      link = generatePath(BankRoutes.BANK_AND_PSP_ACCOUNT_PAGE, {
        accountId: accountId as string,
      });
    } else {
      link = generatePath(BankRoutes.BANK_AND_PSP_EXTERNAL, {
        bankId: bankId || '',
      });
    }

    setTimeout(() => {
      navigate(`${link}${search || ''}`);
    }, 100);
  };

  useEffect(() => {
    if (deletePaymentAccountsIsSuccess) {
      setDeleteConfirmationVisible(false);
      setSelectedOptionId('');
    }
  }, [deletePaymentAccountsIsSuccess]);

  useEffect(() => {
    if (externalAccountTopUpOptions) {
      const nextTopUpOptions = externalAccountTopUpOptions?.map((item) => ({
        id: item?.id,
        paymentType: item?.paymentType,
        currencyId: item?.currencyId,
        bankAccountHolderName: item?.bankAccountHolderName,
        accountNumber: item?.accountNumber,
        bankAddress: item?.bankAddress,
        bic: item?.bic,
        iban: item?.iban,
        bankName: item?.bankName,
        thirdPartyReference: item?.thirdPartyReference,
        pspIntegrationAccountId: item?.pspIntegrationAccountId,
        routingCodeEntries:
          item?.routingCodeEntries?.map((code) => ({
            id: uuid(),
            routingCodeKey: code?.routingCodeKey,
            routingCodeValue: code?.routingCodeValue,
          })) || [],
      }));

      setDefaultTopUpOptions(
        nextTopUpOptions as ExternalAccountTopUpOptionFormValues[],
      );
      setTopUpOptions(
        nextTopUpOptions as ExternalAccountTopUpOptionFormValues[],
      );
    }
  }, [externalAccountTopUpOptions]);

  useEffect(() => {
    if (
      activeTab === 'top-up' &&
      (externalAccountId || accountId) &&
      externalAccount?.pspIntegrationPayment?.paymentsTypes?.length &&
      activePermissions?.includes(
        PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_LIST,
      )
    ) {
      getExternalAccountTopUpOptions({
        id: accountPage ? (externalAccountId as string) : (accountId as string),
        // query: `&paymentType=${externalAccount?.pspIntegrationPayment?.paymentsTypes?.join('&paymentType=')}`,
      });
    }
  }, [
    activeTab,
    externalAccountId,
    accountId,
    externalAccount?.pspIntegrationPayment?.paymentsTypes,
  ]);

  useEffect(() => {
    if (
      activeTab === 'change-logs' &&
      !externalChangeLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [activeTab]);

  useEffect(() => {
    if (accountId || externalAccountId) {
      getExternalAccount(
        accountPage ? (externalAccountId as string) : (accountId as string),
      );
    }
  }, [accountId, accountPage]);

  useEffect(() => {
    if (externalAccount) {
      getAccountsBalancesAction(externalAccount?.account?.id);
      setSelectedCurrencies(externalAccount?.currenciesIds || []);
    }
  }, [externalAccount]);

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  useEffect(() => {
    if (externalAccount && paymentTypes && balances && dictionariesCurrencies) {
      setStatus(externalAccount.active ? 'enabled' : 'disabled');
      const nextCurrenciesList: Currency[] = [];
      const types: string[] = [];

      externalAccount?.pspIntegrationPayment?.paymentsTypes?.forEach((item) => {
        if (paymentTypes?.find((typeValue) => typeValue.paymentType === item)) {
          types.push(
            paymentTypes?.find((typeValue) => typeValue.paymentType === item)
              ?.name as string,
          );
        }
      });

      if (generalInfo?.pspIntegration?.name !== 'OPEN_PAYD') {
        const currentPaymentType = paymentTypes?.find(
          (item) =>
            item.paymentType ===
            externalAccount?.pspIntegrationPayment?.paymentsTypes[0],
        );

        if (currentPaymentType) {
          if (
            currentPaymentType.paymentType !==
              PaymentType.CRYPTO_ASSET_PAYMENT ||
            (currentPaymentType.paymentType ===
              PaymentType.CRYPTO_ASSET_PAYMENT &&
              !externalAccount.caspId)
          ) {
            currentPaymentType?.currencies?.edges?.forEach((item) => {
              if (
                balances?.find((balance) => balance.currencyId === item.node.id)
              ) {
                nextCurrenciesList.push(item.node);
              }
            });
          } else if (
            currentPaymentType.paymentType ===
              PaymentType.CRYPTO_ASSET_PAYMENT &&
            externalAccount.caspId &&
            casp
          ) {
            const currentCasp = casp?.find(
              (item) => item.id === externalAccount.caspId,
            );

            if (currentCasp) {
              const caspCurrencies = currentCasp?.currencies?.map(
                (item) => item.currency,
              );

              if (caspCurrencies?.length) {
                caspCurrencies?.forEach((item) => {
                  if (
                    balances?.find((balance) => balance.currencyId === item.id)
                  ) {
                    nextCurrenciesList.push(item);
                  }
                });

                setCurrencies(nextCurrenciesList);
                setAvailableCurrencies(nextCurrenciesList);
              }
            }
          }
        }
      }

      if (generalInfo?.pspIntegration?.name === 'OPEN_PAYD') {
        setIsOpenPayd(true);
        const integrationCurrencies: { name: string; id: string }[] = [];

        generalInfo?.pspIntegration?.pspIntegrationPayments?.forEach((item) => {
          item.currencies?.forEach((currencyItem) => {
            if (
              !integrationCurrencies?.find(
                (integrationCurrency) =>
                  integrationCurrency.id === currencyItem.id,
              )
            ) {
              integrationCurrencies.push(currencyItem.currency);
            }
          });
        });

        if (integrationCurrencies?.length) {
          integrationCurrencies?.forEach((item) => {
            if (balances?.find((balance) => balance.currencyId === item.id)) {
              nextCurrenciesList.push(item as Currency);
            }
          });
        }
      }

      if (nextCurrenciesList?.length) {
        setCurrencies(nextCurrenciesList);
        setAvailableCurrencies(nextCurrenciesList);
      }

      setAvailablePaymentTypes(types);
    }
  }, [externalAccount, paymentTypes, balances, dictionariesCurrencies]);

  useEffect(() => {
    if (
      (updatePspExternalAccountCurrenciesIsSuccess ||
        createPaymentAccountsIsSuccess ||
        updatePaymentAccountsIsSuccess) &&
      !createPaymentAccountsInProgress &&
      !updatePaymentAccountsInProgress
    ) {
      handleClose();
    }
  }, [
    updatePspExternalAccountCurrenciesIsSuccess,
    createPaymentAccountsInProgress,
    updatePaymentAccountsInProgress,
    createPaymentAccountsIsSuccess,
    updatePaymentAccountsIsSuccess,
  ]);

  let content;

  if (externalAccountIsLoading || externalAccountIsFetching) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <>
        {(Boolean(
          externalAccount?.pspIntegrationPaymentAccountsCount &&
          activePermissions?.includes(
            PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_LIST,
          ),
        ) ||
          activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)) && (
          <Tabs
            tabs={[
              {
                name: 'Details',
                value: 'details',
              },
              {
                name: 'Top-up options',
                value: 'top-up',
                hidden:
                  Boolean(
                    externalAccount?.createType === 'AUTO' &&
                    !externalAccount?.pspIntegrationPaymentAccountsCount,
                  ) ||
                  !activePermissions?.includes(
                    PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_LIST,
                  ),
              },
              {
                name: 'Change Log',
                value: 'change-logs',
                hidden: !activePermissions?.includes(
                  PermissionEnum.AUDIT_LOG_LIST,
                ),
              },
            ]}
            activeTab={activeTab}
            onClick={(tab) =>
              setActiveTab(tab as 'details' | 'top-up' | 'change-logs')
            }
            wrapperClassName={cn.externalAccountTabs}
          />
        )}
        <Activity mode={activeTab === 'details' ? 'visible' : 'hidden'}>
          <>
            <Select
              name="status"
              value={status}
              title="Status"
              list={[
                { name: 'Enabled', value: 'enabled' },
                { name: 'Disabled', value: 'disabled' },
              ]}
              onChange={(e) => setStatus(e.target.value as string)}
              selectWrapperClassName={cn.statusWrapper}
              disabled={
                !activePermissions?.includes(
                  PermissionEnum.PSP_INTEGRATION_ACCOUNT_EDIT_ACTION,
                )
              }
              fullWidth
            />
            {externalAccount?.pspIntegrationPayment?.paymentsTypes?.includes(
              PaymentType.CRYPTO_ASSET_PAYMENT,
            ) && (
              <Select
                name="caspId"
                value={externalAccount?.caspId}
                title="CASP"
                list={casp?.map((item) => ({
                  name:
                    generalInfo?.caspId &&
                    generalInfo?.caspId === item.id &&
                    !externalAccount.caspId
                      ? `Default from Bank (${item.name})`
                      : item.name,
                  value: item.id,
                }))}
                selectWrapperClassName={cn.statusWrapper}
                placeholder="Select CASP"
                disabled
                fullWidth
              />
            )}
            <div className={cn.infoBlock}>
              <div className={cn.infoBlockRow}>
                <div className={cn.infoBlockRowTitle}>IBAN</div>
                <div className={cn.infoBlockRowValue}>
                  {externalAccount?.iban}
                </div>
              </div>
              <div className={cn.infoBlockRow}>
                <div className={cn.infoBlockRowTitle}>Payment type</div>
                <div className={cn.infoBlockRowValue}>
                  {availablePaymentTypes?.join(', ')}
                </div>
              </div>
              <div className={cn.infoBlockRow}>
                <div className={cn.infoBlockRowTitle}>Customer</div>
                <div className={cn.infoBlockRowValue}>
                  <NavLink
                    to={
                      externalAccount?.account?.organization?.type ===
                      'CORPORATE'
                        ? `/customers/corporate/${externalAccount?.account?.organizationId}`
                        : `/customers/individual/${externalAccount?.account?.organizationId}`
                    }
                    target="_blank"
                    className={cn.infoBlockRowLink}
                  >
                    {externalAccount?.account?.description}
                    <IconArrowRight width={12} height={12} />
                  </NavLink>
                </div>
              </div>
              <div className={cn.infoBlockRow}>
                <div className={cn.infoBlockRowTitle}>Customer Account</div>
                <div className={cn.infoBlockRowValue}>
                  <NavLink
                    to={`/accounts/list/${externalAccount?.accountId}`}
                    target="_blank"
                    className={cn.infoBlockRowLink}
                  >
                    {externalAccount?.account?.fullNumber}
                    <IconArrowRight width={12} height={12} />
                  </NavLink>
                </div>
              </div>
            </div>

            <div className={cn.currenciesTitle}>Available Currencies</div>
            {(isLoading || isFetching) && (
              <div className={cn.balancesLoader}>
                Loading <IconAnimatedDots width={26} height={26} />
              </div>
            )}
            {!isLoading && !isFetching && (
              <div className={cn.searchInputWrapper}>
                <IconSearch />
                <InputText
                  name="searchValue"
                  placeholder="Enter currency"
                  className={cn.searchInput}
                  value={searchValue}
                  onChange={(e) => onSearch(e.target.value)}
                  size={InputSize.SMALL}
                />
              </div>
            )}
            {!isLoading && !isFetching && (
              <>
                {dictionariesCurrencies
                  ?.filter((item) => selectedCurrencies?.includes(item.id))
                  ?.map((item) => (
                    <div className={cn.currencyItem} key={item.id}>
                      <Checkbox
                        name={`currency-${item.id}`}
                        type="checkbox"
                        checked={Boolean(
                          selectedCurrencies?.find(
                            (currency) => currency === item.id,
                          ),
                        )}
                        title={item.name}
                        onChange={(e) => {
                          let nextCurrencies = ([] as string[]).concat(
                            selectedCurrencies,
                          );

                          if (e.target.checked) {
                            nextCurrencies.push(item.id);
                          } else {
                            nextCurrencies = nextCurrencies?.filter(
                              (currency) => currency !== item.id,
                            );
                          }

                          setSelectedCurrencies(nextCurrencies);
                        }}
                        disabled={isOpenPayd}
                      />
                    </div>
                  ))}
                {currencies
                  ?.filter((item) => !selectedCurrencies.includes(item.id))
                  ?.map((item) => (
                    <div className={cn.currencyItem} key={item.id}>
                      <Checkbox
                        name={`currency-${item.id}`}
                        type="checkbox"
                        title={item.name}
                        checked={Boolean(
                          selectedCurrencies?.find(
                            (currency) => currency === item.id,
                          ),
                        )}
                        onChange={() => {
                          let nextCurrencies = ([] as string[]).concat(
                            selectedCurrencies,
                          );

                          if (
                            nextCurrencies?.find(
                              (currency) => currency === item.id,
                            )
                          ) {
                            nextCurrencies = nextCurrencies.filter(
                              (currency) => currency !== item.id,
                            );
                          } else {
                            nextCurrencies.push(item.id);
                          }

                          setSelectedCurrencies(nextCurrencies);
                        }}
                        disabled={isOpenPayd}
                      />
                    </div>
                  ))}
              </>
            )}
          </>
        </Activity>
        <Activity mode={activeTab === 'top-up' ? 'visible' : 'hidden'}>
          <>
            {(topUpIsLoading || topUpIsFetching) && <SidePanelLoader />}
            {!topUpIsLoading && !topUpIsFetching && (
              <>
                <div className={cn.topUpOptionsTitleWrapper}>
                  <div className={cn.topUpOptionsTitleWrapperLeft}>
                    <div className={cn.topUpOptionsTitle}>Top-up options</div>
                    <div className={cn.topUpOptionsDescription}>
                      Payment details the customer uses to top up this account
                    </div>
                  </div>
                  {externalAccount?.createType === 'MANUAL' &&
                    activePermissions?.includes(
                      PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_ADD_ACTION,
                    ) && (
                      <ButtonSecondary
                        className={`${cn.createOptionButton} button-white`}
                        icon={<IconExpand />}
                        text="Add option"
                        size={ButtonSize.SMALL}
                        onClick={() => {
                          const nextOptions = (
                            [] as ExternalAccountTopUpOptionFormValues[]
                          ).concat(topUpOptions);

                          nextOptions.unshift({
                            id: `option-${uuid()}`,
                            paymentType: '',
                            currencyId: '',
                            bankAccountHolderName: '',
                            accountNumber: '',
                            bankAddress: '',
                            bic: '',
                            iban: '',
                            thirdPartyReference: '',
                            bankName: '',
                            routingCodeEntries: [],
                          });

                          setTopUpOptions(nextOptions);
                        }}
                      />
                    )}
                </div>
                {!topUpOptions?.length && (
                  <div className={cn.emptyTopUpOption}>
                    No top up options yet
                  </div>
                )}
                {topUpOptions &&
                  topUpOptions?.length > 0 &&
                  topUpOptions?.map((item) => (
                    <ExternalAccountTopUpOptionItem
                      key={item.id}
                      topUpOption={item}
                      paymentTypes={paymentTypes}
                      currencies={currenciesList}
                      setSelectedOptionId={setSelectedOptionId}
                      setDeleteConfirmationVisible={
                        setDeleteConfirmationVisible
                      }
                      activePermissions={activePermissions}
                      isEditable={
                        externalAccount?.createType === 'MANUAL' &&
                        activePermissions?.includes(
                          PermissionEnum.PSP_INTEGRATION_PAYMENT_ACCOUNT_EDIT_ACTION,
                        )
                      }
                      newOption={Boolean(item?.id?.includes('option'))}
                      setTopUpOptions={setTopUpOptions}
                      topUpOptions={topUpOptions}
                      optionsError={optionsErrors?.find(
                        (error) => error?.id === item.id,
                      )}
                      optionsErrors={optionsErrors}
                      setOptionsErrors={setOptionsErrors}
                    />
                  ))}
              </>
            )}
          </>
        </Activity>
        <Activity mode={activeTab === 'change-logs' ? 'visible' : 'hidden'}>
          <>
            {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <ChangeLogs
                entityType="PSP_INTEGRATION_ACCOUNT"
                entityId={accountId as string}
                changeLogsIsLoading={changeLogsIsLoading}
                changeLogs={externalChangeLogs}
                activePermission={activePermissions}
                users={users}
                pagination={externalChangeLogsPagination}
                changeLogsIsFetching={changeLogsIsFetching}
                getChangeLogs={getChangeLogsAction}
              />
            )}
            {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <PermissionDenied />
            )}
          </>
        </Activity>
      </>
    );
  }

  return (
    <>
      <SidePanel
        isOpen={panelIsOpen}
        type={SidePanelType.Accounts}
        onClose={() => {
          if (isDirty) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        errorSidepanel={globalError}
        title="External Account settings"
        sidepanelExpandedKey="bank-external-item"
        footer={
          <>
            {activePermissions?.includes(
              PermissionEnum.PSP_INTEGRATION_ACCOUNT_EDIT_ACTION,
            ) && (
              <ButtonPrimary
                text={
                  requestInProgress ||
                  updatePspExternalAccountCurrenciesInProgress ||
                  createPaymentAccountsInProgress ||
                  updatePaymentAccountsInProgress
                    ? 'Loading...'
                    : 'Save'
                }
                disabled={
                  !isDirty ||
                  requestInProgress ||
                  updatePspExternalAccountCurrenciesInProgress ||
                  createPaymentAccountsInProgress ||
                  updatePaymentAccountsInProgress
                }
                onClick={onSubmit}
                bold
              />
            )}

            <ButtonSecondary
              text="Close"
              className="button-white"
              onClick={() => {
                if (status && prevStatus && prevStatus !== status) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              bold
            />
          </>
        }
        closeOnClickOutside
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <Popup
        title="Delete this option?"
        description=""
        onCancelClick={() => {
          setDeleteConfirmationVisible(false);
          setSelectedOptionId('');
        }}
        onOkClick={deletePaymentAccountsAction}
        cancelButtonText="Cancel"
        okButtonText={
          deletePaymentAccountsInProgress ? 'Loading..' : 'Yes, Delete'
        }
        okButtonDisabled={deletePaymentAccountsInProgress}
        isVisible={deleteConfirmationVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default ExternalAccount;
